import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Markup = () => {
   // State variables for the markup calculation
   const [cost, setCost] = useState('');
   const [sellingPrice, setSellingPrice] = useState('');
   const [markupPercentage, setMarkupPercentage] = useState(0);
 
   // Event handlers to update state values
   const handleCostChange = (e) => setCost(parseFloat(e.target.value) || 0);
   const handleSellingPriceChange = (e) => setSellingPrice(parseFloat(e.target.value) || 0);
 
   // useEffect hook to calculate markup when either cost or selling price changes
   useEffect(() => {
     if (cost && sellingPrice) {
       const markup = ((sellingPrice - cost) / cost) * 100;
       setMarkupPercentage(markup);
     } else {
       setMarkupPercentage(0);
     }
     // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
   }, [cost, sellingPrice]); // Dependency array: runs when cost or sellingPrice changes
 

  return (
    <Layout title="Markup Calculator" path="markup">
    <section className="section-padding" id="markup-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Markup Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Markup Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="cost" className="form-label">
                        Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Cost:</strong> The initial cost of the product or service."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="cost"
                        placeholder="Enter product cost"
                        value={cost}
                        onChange={handleCostChange}
                      />
                    </div>

                    {/* Selling Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="sellingPrice" className="form-label">
                        Selling Price (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Selling Price:</strong> The price at which you intend to sell the product or service."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="sellingPrice"
                        placeholder="Enter selling price"
                        value={sellingPrice}
                        onChange={handleSellingPriceChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Markup Percentage:{" "}
                        <span className="fw-bold ms-3">
                          {markupPercentage.toFixed(2)}%
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Markup Calculator
                </h3>

                {/* What is a Markup Calculator? */}
                <h4 className="mt-3 mb-3">What is a Markup Calculator?</h4>
                <p>
                  A **Markup Calculator** helps businesses determine the markup percentage required to achieve a desired selling price based on the cost of a product or service.
                  {/* The formula used is: */}
                </p>
                {/* <pre>Markup = [(Selling Price - Cost) / Cost] x 100</pre> */}

                {/* Why Use a Markup Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Markup Calculator?</h4>
                <p>
                  This calculator helps businesses ensure their pricing strategy is effective, profitable, and competitive. By knowing the markup percentage required to cover costs and generate profit,
                  businesses can better price their products or services.
                </p>

                {/* Benefits of Using a Markup Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Markup Calculator</h4>
                <ul>
                  <li><strong>Assists in Pricing Products:</strong> Helps businesses determine the right price point for their products or services.</li>
                  <li><strong>Ensures Profitability:</strong> Helps businesses set prices that cover costs and generate desired profits.</li>
                  <li><strong>Supports Cost Management:</strong> Helps analyze how changes in cost affect pricing and profitability.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Markup Calculator?</h4>
                <p>
                  The markup calculator is suitable for any business that sells products or services, including:
                </p>
                <ul>
                  <li><strong>Retailers:</strong> Helps set the right price for inventory.</li>
                  <li><strong>Manufacturers:</strong> Assists in calculating the markup needed on produced goods.</li>
                  <li><strong>E-commerce businesses:</strong> Useful for setting prices in an online store, ensuring competitive and profitable pricing.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How do I calculate markup?</h4>
                <p>
                  To calculate the markup, subtract the cost of the product from the selling price, divide that difference by the cost, and then multiply by 100 to get the percentage.
                </p>

                <h4 className="mt-4 mb-3">What’s the difference between markup and profit margin?</h4>
                <p>
                  Markup refers to the percentage added to the cost to determine the selling price, while profit margin refers to the percentage of the selling price that is profit.
                  Both are essential metrics for pricing strategies.
                </p>

                <h4 className="mt-4 mb-3">Why is markup important for businesses?</h4>
                <p>
                  Markup ensures that businesses are able to cover their costs and earn a profit. It helps businesses balance competitive pricing with financial sustainability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Markup;
